import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

import { findBook } from "../data/books";
import { booksPath } from "../lib/paths";

// La descripción de Google se corta en unos 160 caracteres. Cortarla a pelo
// parte la última palabra por la mitad; esto corta en el espacio anterior.
function resumir(texto, max = 158) {
  const t = texto.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const corte = t.slice(0, max - 1);
  return corte.slice(0, corte.lastIndexOf(" ")).replace(/[,;:.—-]$/, "") + "…";
}

const T = {
  de: {
    back: "← Alle Bücher",
    data: "Angaben",
    isbn: "ISBN",
    pages: "Seiten",
    format: "Format",
    price: "Preis",
    lang: "Sprache",
    genre: "Genre",
    setting: "Schauplätze",
    soon: "Erscheint",
    buy: "Kaufen bei",
    bookshop: "Auch in jeder Buchhandlung unter Angabe der ISBN bestellbar.",
    notYet:
      "Das Buch ist noch nicht im Handel. Schreiben Sie mir, wenn Sie benachrichtigt werden möchten, sobald es erscheint.",
    write: "Benachrichtigt werden",
    trade: "Für den Buchhandel",
    tradeText:
      "Bestellbar über den Buchhandel unter der oben genannten ISBN. Für Ansichts- und Presseexemplare sowie Konditionen schreiben Sie bitte an",
    spanish: "Spanisch",
    german: "Deutsch",
  },
  es: {
    back: "← Todos los libros",
    data: "Datos",
    isbn: "ISBN",
    pages: "Páginas",
    format: "Formato",
    price: "Precio",
    lang: "Idioma",
    genre: "Género",
    setting: "Escenarios",
    soon: "Publicación",
    buy: "Comprar en",
    bookshop: "También puedes pedirlo en cualquier librería indicando el ISBN.",
    notYet:
      "El libro todavía no está a la venta. Escríbeme si quieres que te avise en cuanto salga.",
    write: "Avisadme",
    trade: "Para librerías",
    tradeText:
      "Puede pedirse por el canal librero con el ISBN indicado. Para ejemplares de muestra, de prensa y condiciones, escriba a",
    spanish: "Español",
    german: "Alemán",
  },
};

// Los rótulos de los botones de compra, por clave de `links`.
const TIENDAS = {
  amazonEs: "Amazon.es",
  amazonCom: "Amazon.com",
  amazonDe: "Amazon.de",
  kindleEs: "Kindle · Amazon.es",
  kindleCom: "Kindle · Amazon.com",
  kindleDe: "Kindle · Amazon.de",
};

// Quien lee la ficha en español compra en español: España e Hispanoamérica.
// Quien la lee en alemán, en Amazon.de. Enseñar las seis tiendas a todo el
// mundo no ayuda a decidir, y manda a la tienda equivocada a media gente.
const TIENDAS_POR_IDIOMA = {
  es: ["amazonEs", "kindleEs", "amazonCom", "kindleCom"],
  de: ["amazonDe", "kindleDe"],
};

function Dato({ label, value }) {
  if (!value) return null;
  return (
    <div className="py-3 border-b border-tinta/10 last:border-0">
      <dt className="rotulo text-sombra mb-1">{label}</dt>
      <dd className="text-tinta/90">{value}</dd>
    </div>
  );
}

export default function BookDetail({ language = "de" }) {
  const { slug } = useParams();
  const book = findBook(slug);
  const t = T[language];

  if (!book) return <Navigate to={booksPath(language)} replace />;

  const c = book[language];
  // En el orden del idioma, y solo las tiendas que le tocan.
  const enlaces = (TIENDAS_POR_IDIOMA[language] || [])
    .map((k) => [k, (book.links || {})[k]])
    .filter(([, v]) => v);

  return (
    <div className="bg-papel text-tinta min-h-[70vh]">
      <Helmet>
        <title>{`${book.title} – ${book.author} | Blauer Wolf Verlag`}</title>
        <meta name="description" content={resumir(`${c.tagline} ${c.blurb[0]}`)} />
        <meta property="og:type" content="book" />
        <meta property="og:title" content={`${book.title} – ${book.author}`} />
        <meta property="og:description" content={c.tagline} />
        <meta property="og:image" content={`https://www.bw-verlag.at${book.cover}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Book",
            name: book.title,
            ...(book.subtitle ? { alternativeHeadline: book.subtitle } : {}),
            author: { "@type": "Person", name: book.author },
            isbn: book.isbn,
            numberOfPages: book.pages,
            inLanguage: book.bookLanguage,
            bookFormat: "https://schema.org/Paperback",
            publisher: { "@type": "Organization", name: "Blauer Wolf Verlag" },
            datePublished: String(book.year),
            image: `https://www.bw-verlag.at${book.cover}`,
            description: c.tagline,
            ...(book.available && enlaces.length
              ? {
                  offers: {
                    "@type": "Offer",
                    price: String(book.price).replace(/[^\d,]/g, "").replace(",", "."),
                    priceCurrency: "EUR",
                    availability: "https://schema.org/InStock",
                    url: enlaces[0][1],
                  },
                }
              : {}),
          })}
        </script>
      </Helmet>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14 sm:py-20">
        <Link to={booksPath(language)} className="rotulo text-sombra hover:text-tinta transition-colors">
          {t.back}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-12 lg:gap-20 mt-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img
              src={book.cover}
              alt={`${book.title} – ${book.author}`}
              width="300"
              height="464"
              className="w-full max-w-[300px] mx-auto lg:mx-0 shadow-[0_18px_50px_-18px_rgba(18,16,14,0.6)]"
            />

            <dl className="mt-10 border-t border-tinta/20 pt-4">
              <Dato label={t.isbn} value={book.isbn} />
              <Dato label={t.pages} value={book.pages} />
              <Dato label={t.format} value={book.format} />
              <Dato label={t.price} value={book.price} />
              <Dato
                label={t.lang}
                value={book.bookLanguage === "es" ? t.spanish : t.german}
              />
              <Dato label={t.genre} value={c.genre} />
            </dl>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.5 }}
          >
            <h1 className="font-serif text-4xl sm:text-5xl leading-tight tracking-titulo mb-3">
              {book.title}
            </h1>
            {book.subtitle && (
              <p className="font-serif italic text-xl text-tinta/70 mb-3 max-w-prosa">
                {book.subtitle}
              </p>
            )}
            <p className="text-sombra mb-10">{book.author}</p>

            <p className="font-serif italic text-xl sm:text-2xl text-loboOscuro mb-8">
              {c.tagline}
            </p>

            <div className="max-w-prosa">
              {c.blurb.map((p, i) => (
                <p key={i} className="text-tinta/85 mb-5">
                  {p}
                </p>
              ))}

              <p className="border-l-2 border-lobo pl-6 my-9 text-tinta/90">
                {c.closing}
              </p>

              <p className="text-sm text-sombra mb-10">
                <span className="rotulo mr-2">{t.setting}</span>
                {c.setting}
              </p>
            </div>

            {enlaces.length > 0 ? (
              <div className="mb-10">
                <div className="flex flex-wrap gap-4">
                  {enlaces.map(([k, url]) => (
                    <a
                      key={k}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="rotulo bg-tinta text-hueso px-7 py-3.5 hover:bg-loboOscuro transition-colors"
                    >
                      {t.buy} {TIENDAS[k] || k}
                    </a>
                  ))}
                </div>
                <p className="text-sm text-sombra mt-5 max-w-prosa">
                  {t.bookshop}
                </p>
                {book.saleNote?.[language] && (
                  <p className="text-sm text-sombra mt-5 max-w-prosa">
                    {book.saleNote[language]}
                  </p>
                )}
              </div>
            ) : (
              <div className="border border-tinta/20 p-7 sm:p-8 mb-10 max-w-prosa">
                <p className="rotulo text-loboOscuro mb-3">
                  {t.soon} · {book.releaseLabel[language]}
                </p>
                <p className="text-tinta/80 mb-6">{t.notYet}</p>
                <Link
                  to={`/${language}#kontakt`}
                  className="rotulo inline-block bg-tinta text-hueso px-7 py-3.5 hover:bg-loboOscuro transition-colors"
                >
                  {t.write}
                </Link>
              </div>
            )}

            <p className="text-sm text-sombra mb-10">{c.note}</p>

            <div className="pt-7 border-t border-tinta/15 max-w-prosa">
              <h2 className="rotulo text-loboOscuro mb-3">{t.trade}</h2>
              <p className="text-sm text-tinta/70">
                {t.tradeText}{" "}
                <a
                  className="underline underline-offset-4 hover:text-tinta"
                  href="mailto:office@bw-verlag.at"
                >
                  office@bw-verlag.at
                </a>
                .
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
